import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  BaseOneResponseInterface,
  BulkResponseDataInterface,
} from '../../../../shared/model/interface/crud-response-interface.model';
import * as oeeAppReducer from '../../../oee.reducer';
import * as AppActions from '../../../app/actions';
import { ErrorMessageService } from '../../../../shared/service/error-message.service';
import { IHeartbeatSetting } from '../../../../view/settings/api-automation-settings/heartbeat-settings/heartbeat-settings.model';
import { HeartbeatSettingService } from './heartbeat-settings.service';
import {
  ActionTypes,
  HeartbeatSettingsLoading,
  HeartbeatSettingsLoaded,
  FetchError,
  GetHeartbeatSetting,
  GetHeartbeatSettingCompleted,
  EditHeartbeatSetting,
  EditHeartbeatSettingCompleted,
  AddHeartbeatSetting,
  AddHeartbeatSettingCompleted,
  DeleteHeartbeatSettings,
  DeleteHeartbeatSettingsCompleted,
  MailGroupsDataLoading,
  MailGroupsDataCompleted,
  MailTemplatesDataLoading,
  MailTemplatesDataCompleted,
} from './heartbeat-settings.actions';
import { NotificationGroupsService } from '../../nofication-center/notification-groups/notification-groups.service';
import { NotificationTemplateService } from '../../notification-center/notification-templates/notification-template.service';

@Injectable()
export class HeartbeatSettingEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: HeartbeatSettingService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly errorMessageService: ErrorMessageService,
    private readonly notificationGroupsService: NotificationGroupsService,
    private readonly notificationTemplateService: NotificationTemplateService,
  ) {}

  getDataForDatatable = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.HEARTBEAT_SETTINGS_DATA_LOADING),
      switchMap((payload: HeartbeatSettingsLoading) => {
        const andConditions = [];
        const searchText: string = payload.params.searchText?.trim() ?? '';
        let httpParams: HttpParams = new HttpParams()
          .append('sort', 'id,DESC')
          .append('limit', String(payload.params.perPage || 1000))
          .append('page', String(payload.params.page));

        if (searchText) {
          andConditions.push({
            $or: [
              { heartbeatId: { $cont: searchText } },
              { name: { $cont: searchText } },
              { deviceMac: { $cont: searchText } },
              { deviceInputName: { $cont: searchText } },
            ],
          });
        }

        if (andConditions.length > 0) {
          httpParams = httpParams.set('s', JSON.stringify({ $and: andConditions }));
        }

        if (payload.params.orderBy && payload.params.orderDesc !== undefined) {
          httpParams = httpParams.set('sort', `${payload.params.orderBy},${payload.params.orderDesc}`);
        }

        return this.service.getData(httpParams).pipe(
          switchMap((response) => of(new HeartbeatSettingsLoaded(response))),
          catchError((error) => of(new FetchError(error))),
        );
      }),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.GET_HEARTBEAT_SETTING),
      switchMap((objectData: GetHeartbeatSetting) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getOne(objectData.recordId).pipe(
          switchMap((response: BaseOneResponseInterface<IHeartbeatSetting>) =>
            of(new GetHeartbeatSettingCompleted(response), new AppActions.HideLoader()),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  addOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ADD_HEARTBEAT_SETTING),
      switchMap((objectData: AddHeartbeatSetting) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.addOne(objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<IHeartbeatSetting>) =>
            of(
              new AddHeartbeatSettingCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  editOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.EDIT_HEARTBEAT_SETTING),
      switchMap((objectData: EditHeartbeatSetting) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.editOne(objectData.recordId, objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<IHeartbeatSetting>) =>
            of(
              new EditHeartbeatSettingCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  deleteOnes = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DELETE_HEARTBEAT_SETTINGS),
      switchMap((objectData: DeleteHeartbeatSettings) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.deleteOnes(objectData.payload).pipe(
          switchMap((response: BulkResponseDataInterface) => {
            this.errorMessageService.getTranslatedErrorMessage(response.data);

            return of(
              new DeleteHeartbeatSettingsCompleted(objectData.payload.length > 1, response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  getMailGroupsData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.MAIL_GROUPS_DATA_LOADING),
      switchMap(() =>
        this.notificationGroupsService.getNotificationGroups(new HttpParams().set('limit', '1000')).pipe(
          switchMap((response) => of(new MailGroupsDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getMailTemplatesData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.MAIL_TEMPLATES_DATA_LOADING),
      switchMap(() =>
        this.notificationTemplateService.getNotificationTemplates(new HttpParams().set('limit', '1000')).pipe(
          switchMap((response) => of(new MailTemplatesDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );
}
